/**
 * « Coût politique » affiché à côté de chaque montant (ADR 0011, red team v0.2 B1 : symétrie visible des outils).
 * Unité unique : points de capital politique (même échelle que `measure.political_cost` et `option.political_cost`).
 * Mesures et réponses de crise : payé une fois ; leviers d'impôt, rabot et économies par mission : chaque année.
 * Les valeurs viennent du moteur (`adoptionPoliticalCost`, `leverEffect`, `cutEffect`, exportés) ou des params ; si le moteur
 * v0.3 exporte un assistant `politicalCostPerBn`, il est préféré pour le ratio « par Md€ » (détecté, jamais supposé).
 */
import * as engine from "../../engine/src/index.js";
import type { EventOption, Measure, ParamSet } from "../../engine/src/types.js";
import { NBSP, num } from "./format.js";

export interface PolCost {
  /** Points de capital (> 0 = coût, < 0 = gain). */
  points: number;
  perYear: boolean;
  /** Montant associé (Md€, valeur absolue) pour le ratio « par Md€ » ; absent = pas de ratio. */
  bn?: number;
  /** Ratio fourni par l'assistant moteur (prioritaire sur points / bn). */
  perBn?: number;
}

const finite = (x: unknown): x is number => typeof x === "number" && Number.isFinite(x);

type PerBnHelper = (params: ParamSet, kind: string, id: string, amountBn: number) => number;
/** Assistant moteur v0.3 (supposé) : coût politique par Md€ d'un élément ; null si non exporté. */
export function enginePerBnHelper(): PerBnHelper | null {
  const f = (engine as unknown as Record<string, unknown>)["politicalCostPerBn"];
  return typeof f === "function" ? (f as PerBnHelper) : null;
}

/** Ajoute le ratio de l'assistant moteur s'il existe et répond un nombre fini. */
function withHelper(c: PolCost, params: ParamSet, kind: string, id: string, bn: number): PolCost {
  const h = enginePerBnHelper();
  if (!h) return c;
  try {
    const r = h(params, kind, id, bn);
    return finite(r) ? { ...c, perBn: r } : c;
  } catch {
    return c;
  }
}

/** Mesure : coût d'adoption tel que le moteur le prélève (gains des mesures populaires pondérés). */
export function measurePol(params: ParamSet, m0: Measure, regimeBn: number, variantId?: string): PolCost {
  let m = m0;
  if (variantId) {
    try {
      m = engine.applyVariant(m0, variantId);
    } catch {
      m = m0;
    }
  }
  let points = m.political_cost;
  try {
    const v = engine.adoptionPoliticalCost(params, [m]);
    if (finite(v)) points = v;
  } catch {
    /* params incomplets (mock) : valeur du catalogue */
  }
  return withHelper({ points, perYear: false, bn: Math.abs(regimeBn) }, params, "measure", m.id, regimeBn);
}

/** Levier d'impôt (Md€ en régime, négatif = baisse) : coût annuel. */
export function leverPol(params: ParamSet, id: string, bn: number): PolCost {
  let points = 0;
  try {
    points = engine.leverEffect(params, id, bn).political_cost;
  } catch {
    points = (params[`lev_${id}_political_cost_per_bn`]?.value ?? 0) * bn;
  }
  return withHelper({ points, perYear: true, bn: Math.abs(bn) }, params, "lever", id, bn);
}

/** Rabot transversal (part 0–1) : coût annuel ; `bnPerPt` = Md€ économisés par point (aperçu du moteur). */
export function cutPol(params: ParamSet, share: number, bnPerPt?: number): PolCost {
  let points = 0;
  try {
    points = engine.cutEffect(params, share, 0).political_cost;
  } catch {
    points = (params.cut_political_cost_per_pt?.value ?? 0) * share * 100;
  }
  return { points, perYear: true, ...(bnPerPt !== undefined ? { bn: Math.abs(bnPerPt * share * 100) } : {}) };
}

/** Économie ciblée sur un groupe de missions (%), coût annuel par point (param du groupe, sinon celui du rabot). */
export function missionCutPol(polPerPt: number | undefined, pct: number, bn?: number): PolCost {
  return { points: (polPerPt ?? 0) * pct, perYear: true, ...(bn !== undefined ? { bn: Math.abs(bn) } : {}) };
}

/** Réponse de crise : coût immédiat (risque éventuel non compris, affiché à part). */
export function optionPol(o: EventOption, bnTotal: number): PolCost {
  return { points: o.political_cost, perYear: false, bn: Math.abs(bnTotal) };
}

/**
 * Phrase courte, identique partout : « coût politique 3 pts », « gain politique 2 pts/an », « coût politique 0 ».
 * Avec `ratio`, ajoute « (0,45 pt par Md€) » quand le montant est connu et non nul.
 */
export function polText(c: PolCost, opts: { ratio?: boolean } = {}): string {
  const p = c.points;
  const zero = Math.abs(p) < 0.05;
  const word = zero || p > 0 ? "coût politique" : "gain politique";
  const pts = zero ? "0" : `${num(Math.abs(p), Math.abs(p) < 10 && Math.abs(p) % 1 !== 0 ? 1 : 0)}${NBSP}pt${Math.abs(p) >= 2 ? "s" : ""}`;
  const r = c.perBn ?? (c.bn !== undefined && c.bn > 0.05 ? Math.abs(p) / c.bn : undefined);
  const ratio = opts.ratio && !zero && r !== undefined ? ` (${num(Math.abs(r), 2)} par Md€)` : "";
  return `${word} ${pts}${c.perYear && !zero ? "/an" : ""}${ratio}`;
}

/** Pastille HTML neutre (pas de rouge / vert : un coût politique n'est ni bon ni mauvais par construction). */
export function polPill(c: PolCost, opts: { ratio?: boolean } = {}): string {
  return `<span class="pol" title="Points de capital politique ${c.perYear ? "consommés chaque année" : "consommés une fois, au vote"}">${polText(c, opts)}</span>`;
}
